import { useState, useEffect } from 'react';
import { Row, Col, Button, Modal, Card, Spinner, Pagination } from 'react-bootstrap';
import { useApiClient } from '../../ApiService/useApiClient';

const DAYS_PER_PAGE = 3; // Number of days per page

const CustomerPreviousVisits = ({ CustomerID }) => {

  const api = useApiClient();

  const [tasksByDate, setTasksByDate] = useState({});
  const [dateList, setDateList] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!CustomerID) {
          setError('Customer ID is required');
          setLoading(false);
          return;
        }

        const CompleteURL = `HMCareTask/previoustasklist/${CustomerID}/`;
       
        const response = await api.get(CompleteURL);

        if (response.status === 200 && Array.isArray(response.data)) {
          // Group tasks by taskdate
          const grouped = {};
          response.data.forEach(task => {
            if (!grouped[task.taskdate]) grouped[task.taskdate] = [];
            grouped[task.taskdate].push(task);
          });
          // Sort dates descending (most recent first)
          const sortedDates = Object.keys(grouped).sort((a, b) => new Date(b) - new Date(a));
          setTasksByDate(grouped);
          setDateList(sortedDates);
        } else {
          setError('Invalid data format received');
        }
      } catch (error) {
        setError(error.message || 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [CustomerID]);

  // Pagination logic (by day)
  const totalPages = Math.ceil(dateList.length / DAYS_PER_PAGE);
  const paginatedDates = dateList.slice(
    (currentPage - 1) * DAYS_PER_PAGE,
    currentPage * DAYS_PER_PAGE
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleViewDetails = (task) => {
    setSelectedTask(task);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedTask(null);
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger">{error}</div>
    );
  }

  return (
    <>
      {paginatedDates.map(date => (
        <div key={date} className="mb-4">
          <h5 className="mb-3">{date}</h5>
          <Row className="g-4">
            {tasksByDate[date].map((task) => (
              <Col key={task.taskid} sm={12} md={6} lg={4}>
                <Card className="mb-3 shadow-sm">
                  <Card.Body>
                    <Card.Title>{task.clientname}</Card.Title>
                    <Card.Subtitle className="mb-2 text-muted">
                      {task.start_time} - {task.end_time}
                    </Card.Subtitle>
                    <div className="mb-2">
                      <span className="badge bg-primary me-2">{task.status}</span>
                      <span>{task.service_name}</span>
                    </div>
                    <div>
                      <strong>Carer(s): </strong>
                      {task.assignedAgent &&
                        Object.values(task.assignedAgent).join(', ')}
                    </div>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="mt-3"
                      onClick={() => handleViewDetails(task)}
                    >
                      View Details
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      ))}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-center my-4">
          <Pagination>
            <Pagination.Prev
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            />
            {[...Array(totalPages)].map((_, idx) => (
              <Pagination.Item
                key={idx + 1}
                active={currentPage === idx + 1}
                onClick={() => handlePageChange(idx + 1)}
              >
                {idx + 1}
              </Pagination.Item>
            ))}
            <Pagination.Next
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            />
          </Pagination>
        </div>
      )}

      {/* Detail View Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Task Details - {selectedTask?.taskid}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTask && (
            <div>
              <h5 className="mb-3">{selectedTask.clientname}</h5>
              <div className="row">
                <div className="col-md-6">
                  <p><strong>Date:</strong> {selectedTask.taskdate}</p>
                  <p><strong>Time:</strong> {selectedTask.start_time} - {selectedTask.end_time}</p>
                  <p><strong>Duration:</strong> {selectedTask.no_of_hours} hours</p>
                </div>
                <div className="col-md-6">
                  <p><strong>Service:</strong> {selectedTask.service_name}</p>
                  <p><strong>Status:</strong> {selectedTask.status}</p>
                </div>
              </div>

              <h6 className="mt-4">Visits Details</h6>
              <div className="list-group">
                {(selectedTask.dTOAgentAssignments || selectedTask.dtoAgentAssignments || []).map((assignment) => (
                  <div key={assignment.agentId} className="list-group-item">
                    <div className="d-flex justify-content-between">
                      <div>
                        <strong>{assignment.agentName}</strong>
                        <div className="text-muted small">
                          Checked-in: {assignment.checkInDateTime ? new Date(assignment.checkInDateTime).toLocaleString() : 'N/A'}
                        </div>
                        {assignment.checkOutDateTime && (
                          <div className="text-muted small">
                            Checked-out: {new Date(assignment.checkOutDateTime).toLocaleString()}
                          </div>
                        )}

                        <strong style={{ paddingTop: "5px" }}>Completion Notes</strong>
                        <div className="text-muted small">
                          Services provided : {assignment.completion_option ?? 'N/A'}
                        </div>
                        <div className="text-muted small">
                          Completion Notes: {assignment.completion_notes ?? 'N/A'}
                        </div>
                      </div>
                      {assignment.completion_datetime && (
                        <div className="text-end">
                          <div className="badge bg-success">Completed</div>
                        </div>
                      )}
                    </div>
                    {assignment.completion_notes && (
                      <div className="mt-2 small">
                        <strong>Notes:</strong> {assignment.completion_notes}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CustomerPreviousVisits;
